/** Purchase order detail → Create Receipt eligibility and payloads. */

import {
  buildConsignmentToReceiptPayload,
  effectiveReceiveQty,
  getEditedReceiveQtyText,
  initialCheckedReceiptLineIds,
  parseReceiveQtyInput,
  toQtyNumber,
  validateReceiveLineQty,
} from "./createReceiptFromShipmentHelpers";

export {
  effectiveReceiveQty,
  getEditedReceiveQtyText,
  initialCheckedReceiptLineIds,
  parseReceiveQtyInput,
  validateReceiveLineQty,
};

export const CREATE_RECEIPT_BLOCKED_PO_STATUSES = ["Cancelled"];

export const FULLY_RECEIPTED_PO_STATUS = "Full Receipt Created";

export const purchaseOrderStatusName = status => {
  if (!status) {
    return "";
  }
  if (typeof status === "string") {
    return status;
  }
  return status.name || status.id || "";
};

export const isFullyReceiptedPurchaseOrder = status =>
  purchaseOrderStatusName(status) === FULLY_RECEIPTED_PO_STATUS;

export const isDropshipPurchaseOrder = orderData => {
  const type = orderData?.type;
  if (!type) {
    return false;
  }
  if (typeof type === "object") {
    return type.id === "dropship";
  }
  return type === "dropship";
};

/**
 * @param {{ id?: number, status?: string, name?: string, type?: object|string, wh_opr_type?: object|null }} orderData
 * @returns {{ ok: boolean, reason: string }}
 */
export const createPurchaseReceiptDetailState = orderData => {
  if (!orderData?.id) {
    return { ok: false, reason: "Purchase order is not loaded." };
  }

  const statusName = purchaseOrderStatusName(orderData.status);
  if (CREATE_RECEIPT_BLOCKED_PO_STATUSES.includes(statusName)) {
    const label = orderData.name || "This purchase order";
    return {
      ok: false,
      reason: `Cannot create a receipt for ${label} (${statusName}).`,
    };
  }

  if (isDropshipPurchaseOrder(orderData)) {
    return { ok: false, reason: "Cannot create receipt for Dropship orders." };
  }

  if (!orderData.wh_opr_type) {
    return {
      ok: false,
      reason: "Purchase order must have a warehouse operation type to be receipted.",
    };
  }

  return { ok: true, reason: "" };
};

/**
 * @param {{ allocated_qty?: number|string, receipt_created_qty?: number|string }} line
 * @returns {number}
 */
export const purchaseLineOpenReceiptQty = line => {
  const allocated = toQtyNumber(line.allocated_qty);
  const receiptCreated = toQtyNumber(line.receipt_created_qty);
  const open = Math.floor(allocated - receiptCreated);
  return open > 0 ? open : 0;
};

/**
 * @param {object} line Purchase order line from detail API.
 * @returns {object}
 */
export const mapPurchaseOrderLineToReceiptLine = line => ({
  line_id: line.id,
  order_number: line.line_number ?? "",
  product_number: line.product?.number ?? line.product_number ?? "",
  product_name: line.description ?? line.product?.description ?? "",
  dispatched_qty: toQtyNumber(line.dispatched_qty),
  received_qty: toQtyNumber(line.receipt_created_qty),
  open_qty: purchaseLineOpenReceiptQty(line),
});

/**
 * @param {{ id: number, name?: string, status?: string, items?: object[] }} order
 * @returns {{ groups: Array<object>, skipped_lines: Array<object> }}
 */
export const buildReceiptPreviewFromPurchaseOrder = order => {
  const lines = (order.items || []).map(mapPurchaseOrderLineToReceiptLine);
  return {
    groups: [
      {
        consignment_id: order.id,
        shipment_name: order.name,
        reference: order.name,
        fully_receipted: isFullyReceiptedPurchaseOrder(order.status),
        lines,
      },
    ],
    skipped_lines: [],
  };
};

/**
 * @param {{
 *   checkedLineIds: Iterable<number>,
 *   lineQuantities?: Record<number, string>,
 *   linesById: Record<number, { open_qty: number }>,
 * }} params
 * @returns {{ items: Array<{ po_line_id: number, receipted_qty: number }> }}
 */
export const buildPurchaseOrderToReceiptPayload = ({
  checkedLineIds,
  lineQuantities,
  linesById,
}) => {
  const consignmentPayload = buildConsignmentToReceiptPayload({
    checkedLineIds,
    lineQuantities,
    linesById,
  });

  return {
    items: consignmentPayload.receive_qty.map(row => ({
      po_line_id: row.id,
      receipted_qty: row.receive_qty,
    })),
  };
};

/**
 * @param {object|undefined} response
 * @returns {{ receiptId?: number, receiptName?: string }}
 */
export const receiptFromPurchaseOrderResponse = response => {
  if (!response) {
    return { receiptId: undefined, receiptName: undefined };
  }
  if (response.receipt_id != null) {
    return { receiptId: response.receipt_id, receiptName: response.receipt_name };
  }
  const receipts = response.receipts;
  if (Array.isArray(receipts) && receipts.length) {
    const first = receipts[0];
    return { receiptId: first.receipt_id, receiptName: first.receipt_name };
  }
  return { receiptId: undefined, receiptName: undefined };
};
